/* OVATIME · The Golden One PDP V19 */
(() => {
  "use strict";

  const HERO_SELECTOR = ".golden-smartrr.tgo19-hero";
  const SECTION_ORDER = [
    "tgo_target_groups_V13",
    "tgo_athletes_V13",
    "custom_golden_statistics_KyfCcq",
    "tgo_quality_routine_V13",
    "tgo_why_V13",
    "tgo_journey_V13",
    "custom_golden_text_image_trust_slider_ry4Bjw",
    "tgo_compare_V13",
    "tgo_reviews_V13",
    "custom_golden_sections_navigation_xPGgNA",
    "custom_golden_image_with_text_aCitTp",
    "custom_golden_image_with_text_fCKaXR",
    "custom_golden_image_with_text_BGgpcL",
    "custom_golden_image_with_text_ckLw4q",
    "custom_golden_image_with_text_XFB9TA",
    "custom_golden_image_with_text_ezab6i",
    "custom_golden_bottom_cta_eUrMYJ",
    "tgo_faq_V13",
    "related-products",
    "custom_golden_sticky_atc_Q3AWhL",
  ];

  function enforceSectionOrder(hero) {
    const heroSection = hero.closest(".shopify-section");
    const parent = heroSection?.parentElement;
    if (!heroSection || !parent) return false;

    let anchor = heroSection;
    SECTION_ORDER.forEach((sectionKey) => {
      const section = parent.querySelector(
        `.shopify-section[id$="__${sectionKey}"]`,
      );
      if (!section || section.parentElement !== parent) return;
      if (section.previousElementSibling !== anchor) {
        anchor.insertAdjacentElement("afterend", section);
      }
      anchor = section;
    });

    return true;
  }
  const PLAN_IMAGE_12 =
    "https://cdn.shopify.com/s/files/1/0705/3623/3226/files/Produkt-3.png?v=1785250028";
  const PLAN_IMAGE_SINGLE =
    "https://cdn.shopify.com/s/files/1/0705/3623/3226/files/Produkt-1.png?v=1785250028";
  const VIDEO_IMAGE =
    "https://cdn.shopify.com/s/files/1/0705/3623/3226/files/Cebio_trinkt_The_Golden_One.webp?v=1783497119&width=900";

  function assetUrl(filename) {
    const script = [...document.scripts].find((item) =>
      item.src.includes("tgo-pdp-v19.js"),
    );
    return script ? new URL(filename, script.src).href : filename;
  }

  /* ------------------------------------------------------------------
   * Shared purchase state — the hero buy box is the single source of
   * truth for every buy path on the page (hero CTA, sticky bar, bottom
   * CTA). Exposed as window.TGOPurchase; consumers listen for
   * "tgo:purchase-ready" and "tgo:plan-change" on document.
   * ------------------------------------------------------------------ */
  const purchaseState = {
    hero: null,
    select: null, // (planKey) => void — set by initialiseDynamicPurchase
    add: null, // (options) => Promise<cart> — set by initialiseCart
    readyAnnounced: false,
  };

  function readSelection(hero) {
    const selected = hero?.querySelector(".golden-smartrr__option.selected");
    if (!selected) return null;

    const option = selected.dataset.option || "onetime";
    const variantId = Number.parseInt(selected.dataset.variantId, 10);
    const sellingPlan = Number.parseInt(selected.dataset.sellingPlan, 10);
    const quantity = Number.parseInt(selected.dataset.quantity, 10);
    let key = selected.dataset.planKey;
    if (!key) {
      key = selected.classList.contains("golden-smartrr__benefits-box")
        ? "twelve"
        : option === "subscription"
          ? "four"
          : "once";
    }

    return {
      key,
      option,
      variantId: Number.isFinite(variantId) ? variantId : null,
      sellingPlan:
        option === "subscription" && Number.isFinite(sellingPlan)
          ? sellingPlan
          : null,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      element: selected,
    };
  }

  function buildCartItem(selection) {
    if (!selection || !selection.variantId) return null;
    // Fail closed: a subscription option without a resolvable selling plan
    // must never degrade into a one-time purchase.
    if (selection.option === "subscription" && !selection.sellingPlan) {
      return null;
    }
    const item = { id: selection.variantId, quantity: selection.quantity };
    if (selection.sellingPlan) item.selling_plan = selection.sellingPlan;
    return item;
  }

  function welcomeKitVariantId(hero) {
    const root = hero?.querySelector("[data-tgo20-purchase]");
    const id = Number.parseInt(root?.dataset.welcomeKitVariant || "", 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  async function buildWelcomeKitItem(hero, selection) {
    // Only the 12-week routine (plan key "twelve") gets the free Welcome Kit
    // as an extra one-time line — mirrors the landing page and the automatic
    // "Buy X get Y" discount (min. spend 113 €, prices the kit at 0 €).
    // The line carries no selling plan, so it never enters the subscription
    // contract — renewals stay kit-free by construction.
    if (
      !selection ||
      selection.option !== "subscription" ||
      selection.key !== "twelve"
    ) {
      return null;
    }
    const kitVariant = welcomeKitVariantId(hero);
    if (!kitVariant) return null;

    try {
      const response = await window.fetch(
        `${window.Shopify?.routes?.root || "/"}cart.js`,
        { headers: { Accept: "application/json" } },
      );
      const cart = await response.json();
      const alreadyInCart = (cart.items || []).some(
        (line) =>
          line.id === kitVariant ||
          (line.properties && line.properties._welcome_kit),
      );
      if (alreadyInCart) return null;
    } catch (error) {
      // If the cart cannot be read, still try to add the kit; performAdd
      // retries without it when the combined request fails.
    }

    return {
      id: kitVariant,
      quantity: 1,
      properties: { _welcome_kit: "true" },
    };
  }

  function isPurchaseReady() {
    return Boolean(
      purchaseState.hero && purchaseState.select && purchaseState.add,
    );
  }

  function announcePurchaseReady() {
    if (purchaseState.readyAnnounced || !isPurchaseReady()) return;
    purchaseState.readyAnnounced = true;
    document.dispatchEvent(
      new CustomEvent("tgo:purchase-ready", {
        detail: readSelection(purchaseState.hero),
      }),
    );
  }

  window.TGOPurchase = {
    isReady: isPurchaseReady,
    getSelection: () => readSelection(purchaseState.hero),
    getCartItem: () => buildCartItem(readSelection(purchaseState.hero)),
    select: (planKey) => {
      if (purchaseState.select) purchaseState.select(planKey);
      return readSelection(purchaseState.hero);
    },
    addToCart: (options) =>
      purchaseState.add
        ? purchaseState.add(options)
        : Promise.reject(new Error("The Golden One purchase is not ready")),
  };

  function initialiseGallery(hero) {
    const root = hero?.querySelector("[data-tgo19-gallery]");
    const container = root?.querySelector("[data-tgo19-gallery-swiper]");
    const thumbRail = root?.querySelector(".tgo19-gallery__thumbs");
    const thumbs = root
      ? [...root.querySelectorAll("[data-tgo19-gallery-thumb]")]
      : [];

    if (
      !root ||
      !container ||
      root.dataset.tgo19GalleryReady === "true" ||
      typeof window.Swiper === "undefined"
    ) {
      return false;
    }

    const updateThumbs = (activeIndex) => {
      thumbs.forEach((thumb, index) => {
        const active = index === activeIndex;
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-current", active ? "true" : "false");
      });

      const activeThumb = thumbs[activeIndex];
      if (activeThumb && thumbRail) {
        const target =
          activeThumb.offsetLeft -
          (thumbRail.clientWidth - activeThumb.offsetWidth) / 2;
        thumbRail.scrollTo({
          left: Math.max(0, target),
          behavior: "smooth",
        });
      }
    };

    const gallery = new window.Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 360,
      loop: false,
      roundLengths: true,
      grabCursor: true,
      threshold: 5,
      resistanceRatio: 0.72,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      on: {
        init() {
          updateThumbs(this.activeIndex);
        },
        slideChange() {
          updateThumbs(this.activeIndex);
        },
      },
    });

    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const index = Number.parseInt(thumb.dataset.tgo19GalleryThumb, 10);
        if (Number.isFinite(index)) gallery.slideTo(index);
      });
    });

    root.dataset.tgo19GalleryReady = "true";
    root.tgo19Gallery = gallery;
    return true;
  }

  function setPlanImage(card, src, alt, quantity) {
    const visual = card?.querySelector(".tgo13-plan-product");
    const image = visual?.querySelector(":scope > img");
    if (!visual || !image) return false;

    image.src = src;
    image.removeAttribute("srcset");
    image.removeAttribute("sizes");
    image.alt = alt;
    image.decoding = "async";

    const oldBadge = visual.querySelector(".tgo13-plan-product__qty");
    if (quantity) {
      const badge = oldBadge || document.createElement("b");
      badge.className = "tgo13-plan-product__qty";
      badge.textContent = String(quantity);
      if (!badge.isConnected) visual.appendChild(badge);
    } else {
      oldBadge?.remove();
    }

    return true;
  }

  function stabilisePlans(hero) {
    const image12 = hero.dataset.planImage12 || PLAN_IMAGE_12;
    const image4 = hero.dataset.planImage4 || PLAN_IMAGE_SINGLE;
    const imageSingle = hero.dataset.planImageOnetime || PLAN_IMAGE_SINGLE;
    const twelveWeek = hero.querySelector(".golden-smartrr__benefits-box");
    const fourWeek = hero.querySelector(
      '.golden-smartrr__options > .golden-smartrr__option[data-option="subscription"]',
    );
    const oneTime = hero.querySelector(
      '.golden-smartrr__options > .golden-smartrr__option[data-option="onetime"]',
    );

    // Quantity badges follow the cart quantity of each option (data-quantity).
    const quantityOf = (card, fallback) =>
      Number.parseInt(card?.dataset.quantity, 10) || fallback;

    return [
      setPlanImage(
        twelveWeek,
        image12,
        "12-Wochen-Routine mit drei Dosen The Golden One",
        quantityOf(twelveWeek, 3),
      ),
      setPlanImage(
        fourWeek,
        image4,
        "4-Wochen-Routine mit einer Dose The Golden One",
        quantityOf(fourWeek, 1),
      ),
      setPlanImage(
        oneTime,
        imageSingle,
        "The Golden One Einmalkauf",
        null,
      ),
    ].every(Boolean);
  }

  function createVideoEmbed(url, label) {
    const cleanUrl = String(url || "").trim();
    if (!cleanUrl) return null;

    if (/\.(mp4|webm|mov)(\?|$)/i.test(cleanUrl)) {
      const video = document.createElement("video");
      video.src = cleanUrl;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute("aria-label", label);
      return video;
    }

    const iframe = document.createElement("iframe");
    iframe.title = label;
    iframe.loading = "eager";
    iframe.allow =
      "autoplay; encrypted-media; picture-in-picture; fullscreen";
    iframe.allowFullscreen = true;

    if (/instagram\.com/i.test(cleanUrl)) {
      iframe.src = `${cleanUrl.replace(/\/?(\?.*)?$/, "/")}embed`;
      iframe.className = "tgo19-video-modal__embed is-portrait";
      return iframe;
    }

    if (/facebook\.com/i.test(cleanUrl)) {
      iframe.src = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(cleanUrl)}&show_text=false&autoplay=true`;
      return iframe;
    }

    const youtubeId = cleanUrl.match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([^?&/]+)/i,
    )?.[1];
    if (youtubeId) {
      iframe.src = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`;
      return iframe;
    }

    const vimeoId = cleanUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/i)?.[1];
    if (vimeoId) {
      iframe.src = `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
      return iframe;
    }

    iframe.src = cleanUrl;
    return iframe;
  }

  function openVideoModal(url, label) {
    let dialog = document.querySelector(".tgo19-video-modal");
    if (!dialog) {
      dialog = document.createElement("dialog");
      dialog.className = "tgo19-video-modal";
      dialog.innerHTML = `
        <div class="tgo19-video-modal__shell">
          <div class="tgo19-video-modal__head">
            <strong></strong>
            <button
              class="tgo19-video-modal__close"
              type="button"
              aria-label="Video schließen"
            >
              <img
                src="${assetUrl("icon-close.svg")}"
                alt=""
                width="18"
                height="18"
              >
            </button>
          </div>
          <div class="tgo19-video-modal__media"></div>
        </div>
      `;
      document.body.appendChild(dialog);

      const close = () => {
        if (dialog.open) dialog.close();
      };
      dialog
        .querySelector("button")
        ?.addEventListener("click", close);
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) close();
      });
      dialog.addEventListener("close", () => {
        dialog
          .querySelector(".tgo19-video-modal__media")
          ?.replaceChildren();
        document.documentElement.classList.remove("tgo19-video-is-open");
      });
    }

    const media = dialog.querySelector(".tgo19-video-modal__media");
    const title = dialog.querySelector(".tgo19-video-modal__head strong");
    const embed = createVideoEmbed(url, label);
    if (!media || !title || !embed) return false;

    title.textContent = label;
    media.replaceChildren(embed);
    document.documentElement.classList.add("tgo19-video-is-open");
    dialog.showModal();
    dialog.querySelector("button")?.focus();
    return true;
  }

  function stabiliseVideo(hero) {
    const commerce = hero?.querySelector("[data-tgo-hero-commerce]");
    if (!commerce || commerce.dataset.tgo19VideoReady === "true") {
      return Boolean(commerce);
    }

    const previous = commerce.querySelector(".tgo-hero-video");
    const configuredUrl =
      hero.dataset.videoUrl ||
      (previous instanceof HTMLAnchorElement
        ? previous.getAttribute("href")
        : "");
    const configuredLabel =
      hero.dataset.videoLabel ||
      previous
        ?.querySelector(".tgo-hero-video__label")
        ?.textContent.trim() ||
      "The Golden One erklärt";
    const configuredImage =
      hero.dataset.videoImage ||
      previous?.querySelector("img.tgo-hero-video__image")?.currentSrc ||
      previous?.querySelector("img.tgo-hero-video__image")?.src ||
      VIDEO_IMAGE;

    const card = document.createElement("button");
    card.className = "tgo19-video";
    card.type = "button";
    if (configuredUrl) {
      card.setAttribute("aria-label", `${configuredLabel}: Video öffnen`);
      card.setAttribute("aria-haspopup", "dialog");
      card.addEventListener("click", () => {
        openVideoModal(configuredUrl, configuredLabel);
      });
    } else {
      card.disabled = true;
      card.setAttribute("aria-disabled", "true");
    }

    const media = document.createElement("span");
    media.className = "tgo19-video__media";
    const image = document.createElement("img");
    image.src = configuredImage;
    image.alt = "";
    image.width = 900;
    image.height = 600;
    image.loading = "lazy";
    const play = document.createElement("span");
    play.className = "tgo19-video__play";
    play.setAttribute("aria-hidden", "true");
    const playIcon = document.createElement("img");
    playIcon.src = assetUrl("icon-play.svg");
    playIcon.alt = "";
    playIcon.width = 14;
    playIcon.height = 14;
    play.appendChild(playIcon);
    media.append(image, play);

    const duration = document.createElement("span");
    duration.className = "tgo19-video__duration";
    duration.textContent = "00:30";
    media.appendChild(duration);

    const body = document.createElement("span");
    body.className = "tgo19-video__body";

    const timer = document.createElement("span");
    timer.className = "tgo19-video__timer";
    timer.textContent = "00:30";
    const lead = document.createElement("span");
    lead.className = "tgo19-video__lead";
    lead.textContent = "Sekunden, die alles erklären.";

    const text = document.createElement("span");
    text.className = "tgo19-video__text";
    const title = document.createElement("strong");
    title.className = "tgo19-video__label";
    title.textContent = "The Golden One in 30 Sekunden erklärt.";
    const description = document.createElement("span");
    description.className = "tgo19-video__description";
    description.textContent = "Dosierung · Geschmack · Anwendung";

    const action = document.createElement("span");
    action.className = "tgo19-video__action";
    const actionLabel = document.createElement("span");
    actionLabel.textContent = "Jetzt kurz ansehen";
    const actionArrow = document.createElement("span");
    actionArrow.className = "tgo19-video__action-arrow";
    actionArrow.textContent = "→";
    action.append(actionLabel, actionArrow);

    text.append(title, description);
    body.append(timer, lead, text, action);
    card.append(body, media);

    const grid =
      commerce.querySelector(".tgo-hero-commerce__grid") ||
      document.createElement("div");
    grid.className = "tgo-hero-commerce__grid";
    grid.replaceChildren(card);
    if (!grid.isConnected) commerce.appendChild(grid);

    commerce
      .querySelectorAll(".tgo-hero-benefits, .tgo-hero-proof")
      .forEach((item) => item.remove());
    commerce.dataset.tgo19VideoReady = "true";
    return true;
  }

  function positionVideoUnderGallery(hero) {
    const gallery = hero?.querySelector("[data-tgo19-gallery]");
    const commerce = hero?.querySelector("[data-tgo-hero-commerce]");
    if (!gallery || !commerce) return false;
    if (commerce.parentElement !== gallery) {
      gallery.appendChild(commerce);
    }
    commerce.dataset.tgo19GalleryPlacement = "true";
    return true;
  }

  function cleanHero(hero) {
    hero.querySelector(".tgo13-hero-audiences")?.remove();
  }

  function positionPurchaseProof(hero) {
    const proof = hero?.querySelector("[data-tgo-purchase-proof]");
    const wrapper = hero?.querySelector("[data-tgo-purchase-proof-wrap]");
    const info = hero?.querySelector(".golden-smartrr__info");
    const title = info?.querySelector(".golden-smartrr__title");
    const titleLine1 = title?.querySelector(".golden-smartrr__title-line1");
    const reviews = info?.querySelector(".golden-smartrr__reviews");
    if (!proof || !wrapper || !info || !title || !titleLine1) return false;

    if (proof.parentElement !== wrapper) {
      wrapper.appendChild(proof);
    }
    if (wrapper.parentElement !== info || wrapper.nextElementSibling !== title) {
      info.insertBefore(wrapper, title);
    }
    if (reviews && reviews.previousElementSibling !== titleLine1) {
      titleLine1.insertAdjacentElement("afterend", reviews);
    }

    wrapper.dataset.tgo19HeroPlacement = "true";
    return true;
  }

  function parseDisplayedPrice(value) {
    const match = String(value || "").match(/\d[\d.\s]*,\d{1,2}|\d[\d.\s]*/);
    if (!match) return Number.NaN;
    return Number.parseFloat(
      match[0].replace(/\s/g, "").replace(/\./g, "").replace(",", "."),
    );
  }

  function formatPrice(value) {
    if (!Number.isFinite(value)) return "";
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  function initialiseDynamicPurchase(hero) {
    const root = hero?.querySelector("[data-tgo20-purchase]");
    const ui = root?.querySelector("[data-tgo20-purchase-ui]");
    const sources = root?.querySelector("[data-tgo20-purchase-sources]");
    if (!root || !ui || !sources) return false;

    const sourceByKey = {
      twelve: sources.querySelector(".golden-smartrr__benefits-box"),
      four: sources.querySelector(
        '.golden-smartrr__options > [data-option="subscription"]',
      ),
      once: sources.querySelector(
        '.golden-smartrr__options > [data-option="onetime"]',
      ),
    };
    if (Object.values(sourceByKey).some((source) => !source)) return false;

    const actions = root.querySelector("[data-tgo20-actions]");
    const payments = root.querySelector("[data-tgo20-payments]");
    const cta = hero.querySelector(".tgo19-cta");
    const paymentWrap = hero.querySelector(".golden-smartrr__payment-wrap");
    const redundantTrust = hero.querySelector(".tgo13-hero-trust");
    if (actions && cta && cta.parentElement !== actions) actions.appendChild(cta);
    if (payments && paymentWrap && paymentWrap.parentElement !== payments) {
      payments.appendChild(paymentWrap);
    }
    if (redundantTrust) redundantTrust.hidden = true;

    if (root.dataset.tgo20Ready === "true") return true;

    const selectors = {
      image: root.querySelector("[data-tgo20-product-image]"),
      quantity: root.querySelector("[data-tgo20-product-quantity]"),
      title: root.querySelector("[data-tgo20-title]"),
      meta: root.querySelector("[data-tgo20-meta]"),
      oldPrice: root.querySelector("[data-tgo20-price-old]"),
      currentPrice: root.querySelector("[data-tgo20-price-current]"),
      unit: root.querySelector("[data-tgo20-price-unit]"),
      billing: root.querySelector("[data-tgo20-billing]"),
      portion: root.querySelector("[data-tgo20-portion]"),
      comparisonValue: root.querySelector(
        "[data-tgo20-comparison-value]",
      ),
      comparisonBar: root.querySelector("[data-tgo20-comparison-bar]"),
      comparisonReference: root.querySelector(
        "[data-tgo20-comparison-reference]",
      ),
      saving: root.querySelector("[data-tgo20-saving]"),
      savingNote: root.querySelector("[data-tgo20-saving-note]"),
      benefits: root.querySelector("[data-tgo20-benefits]"),
      schedule: root.querySelector("[data-tgo20-schedule]"),
    };
    const buttons = [...root.querySelectorAll("[data-tgo20-plan]")];
    const ctaLabel = cta?.querySelector(".tgo19-cta__label");

    const readPlan = (key) => {
      const source = sourceByKey[key];
      const title =
        source.querySelector(
          ".golden-smartrr__benefits-box-title, .golden-smartrr__option-title",
        )?.textContent.trim() || "";
      const meta =
        source.querySelector(
          ".golden-smartrr__benefits-box-subtext, .golden-smartrr__option-subtitle",
        )?.textContent.trim() || "";
      const product = source.querySelector(".tgo13-plan-product");
      const image = product?.querySelector(":scope > img");
      const quantity =
        product?.querySelector(".tgo13-plan-product__qty")?.textContent.trim() ||
        "";
      const billing =
        source.querySelector(".tgo13-plan-billing")?.textContent.trim() || "";
      const portion =
        source.querySelector(
          ".golden-smartrr__benefits-box-price-per, .golden-smartrr__price-per",
        )?.textContent.trim() || "";
      const oldPrice =
        source.querySelector(".tgo13-plan-price-old")?.textContent.trim() || "";
      const currentPrice =
        source
          .querySelector(".tgo13-plan-price-current")
          ?.textContent.trim() || "";
      const unit =
        source.querySelector(".tgo13-plan-price-unit")?.textContent.trim() || "";
      const benefitItems =
        key === "once"
          ? []
          : [
              ...source.querySelectorAll(
                ".golden-smartrr__benefits-box-list li, .golden-smartrr__features li",
              ),
            ].slice(0, 4);
      return {
        key,
        source,
        title,
        meta,
        image,
        quantity,
        billing,
        portion,
        oldPrice,
        currentPrice,
        unit,
        benefitItems,
      };
    };

    const plans = {
      twelve: readPlan("twelve"),
      four: readPlan("four"),
      once: readPlan("once"),
    };
    const referencePortion = parseDisplayedPrice(plans.once.portion);

    const renderBenefits = (plan) => {
      if (!selectors.benefits) return;
      selectors.benefits.replaceChildren();
      plan.benefitItems.forEach((item) => {
        const row = document.createElement("span");
        row.className = "tgo20-purchase__benefit";
        const icon = item.querySelector(".tgo13-plan-icon")?.cloneNode(true);
        const copy = document.createElement("span");
        copy.textContent =
          item.querySelector(":scope > span:last-child")?.textContent.trim() ||
          item.textContent.trim();
        if (icon) row.appendChild(icon);
        row.appendChild(copy);
        selectors.benefits.appendChild(row);
      });
      selectors.benefits.hidden = plan.key === "once";
    };

    const renderPlan = (key, triggerSource) => {
      const plan = plans[key];
      if (!plan) return;
      if (triggerSource) {
        plan.source.click();
        // The section's inline script normally moves ".selected" on click;
        // make sure the hidden source cards reflect the choice regardless.
        if (!plan.source.classList.contains("selected")) {
          Object.values(plans).forEach((entry) => {
            entry.source.classList.toggle("selected", entry === plan);
          });
        }
      }

      buttons.forEach((button) => {
        const active = button.dataset.tgo20Plan === key;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-checked", active ? "true" : "false");
      });

      if (selectors.image && plan.image) {
        selectors.image.src = plan.image.currentSrc || plan.image.src;
        selectors.image.alt = plan.image.alt || plan.title;
      }
      if (selectors.quantity) {
        selectors.quantity.textContent = plan.quantity;
        selectors.quantity.hidden = !plan.quantity;
      }
      if (selectors.title) selectors.title.textContent = plan.title;
      if (selectors.meta) selectors.meta.textContent = plan.meta;
      if (selectors.oldPrice) {
        selectors.oldPrice.textContent = plan.oldPrice;
        selectors.oldPrice.hidden = !plan.oldPrice;
      }
      if (selectors.currentPrice)
        selectors.currentPrice.textContent = plan.currentPrice;
      if (selectors.unit)
        selectors.unit.textContent =
          plan.unit || (key === "once" ? "einmalig" : "/mtl.");
      if (selectors.billing)
        selectors.billing.textContent =
          plan.billing ||
          (key === "once" ? `Einmalige Zahlung ${plan.currentPrice}` : "");
      if (selectors.portion) selectors.portion.textContent = plan.portion;

      const selectedPortion = parseDisplayedPrice(plan.portion);
      const saving =
        Number.isFinite(referencePortion) && Number.isFinite(selectedPortion)
          ? Math.max(0, referencePortion - selectedPortion)
          : 0;
      const savingPercent =
        referencePortion > 0 ? Math.round((saving / referencePortion) * 100) : 0;
      if (selectors.comparisonValue)
        selectors.comparisonValue.textContent =
          key === "twelve" ? `${plan.portion} 🔥` : plan.portion;
      if (selectors.comparisonReference)
        selectors.comparisonReference.textContent = plans.once.portion;
      if (selectors.comparisonBar) {
        const ratio =
          referencePortion > 0 && Number.isFinite(selectedPortion)
            ? Math.min(100, (selectedPortion / referencePortion) * 100)
            : 100;
        selectors.comparisonBar.style.setProperty("--tgo20-bar", `${ratio}%`);
      }
      if (selectors.saving) {
        selectors.saving.textContent =
          key === "once"
            ? "Volle Flexibilität"
            : `${formatPrice(saving)} pro Portion`;
      }
      if (selectors.savingNote) {
        selectors.savingNote.textContent =
          key === "once"
            ? "Keine Bindung, kein Abonnement"
            : `${savingPercent} % günstiger als der Einmalkauf`;
      }
      renderBenefits(plan);

      if (selectors.schedule) {
        if (key === "twelve") {
          selectors.schedule.textContent = `Heute ${plan.billing.split(" alle ")[0]} · nächste Lieferung in 12 Wochen · jederzeit pausierbar`;
        } else if (key === "four") {
          selectors.schedule.textContent = `Heute ${plan.billing.split(" alle ")[0]} · nächste Lieferung in 4 Wochen · jederzeit pausierbar`;
        } else {
          selectors.schedule.textContent = `Einmalige Zahlung ${plan.currentPrice} · Kein Abonnement`;
        }
      }

      const labelByKey = {
        twelve: "12-WOCHEN-ROUTINE WÄHLEN",
        four: "4-WOCHEN-ROUTINE WÄHLEN",
        once: "EINMALKAUF WÄHLEN",
      };
      if (cta) cta.dataset.tgo20Label = labelByKey[key];
      if (ctaLabel) ctaLabel.textContent = labelByKey[key];
      root.dataset.tgo20Selection = key;

      document.dispatchEvent(
        new CustomEvent("tgo:plan-change", {
          detail: readSelection(hero),
        }),
      );
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        renderPlan(button.dataset.tgo20Plan, true);
      });
    });

    const initialKey = sourceByKey.twelve.classList.contains("selected")
      ? "twelve"
      : sourceByKey.four.classList.contains("selected")
        ? "four"
        : "once";
    purchaseState.select = (planKey) => {
      if (plans[planKey]) renderPlan(planKey, true);
    };
    renderPlan(initialKey, false);
    root.dataset.tgo20Ready = "true";
    announcePurchaseReady();
    return true;
  }

  function initialiseComparisonProducts() {
    const section = document.querySelector("section.tgo13-compare");
    if (!section || section.dataset.tgo22ProductsReady === "true") return true;

    const panel = section.querySelector(".tgo13-compare__panel");
    const left = section.querySelector(".tgo13-compare__column--dark");
    const right = section.querySelector(".tgo13-compare__column--light");
    const product = section.querySelector(".tgo13-compare__product");
    if (!panel || !left || !right || !product) return false;

    const leftList = left.querySelector("ul");
    if (leftList && product.parentElement !== left) {
      left.insertBefore(product, leftList);
      product.classList.add("tgo22-compare-product--golden");
    }

    if (!right.querySelector(".tgo22-compare-product--generic")) {
      const generic = document.createElement("div");
      generic.className =
        "tgo22-compare-product tgo22-compare-product--generic";
      generic.setAttribute("aria-label", "Typisches No-Name Green Powder");
      const visual = document.createElement("span");
      visual.className = "tgo22-compare-product__visual";
      visual.setAttribute("aria-hidden", "true");
      const badge = document.createElement("span");
      badge.className = "tgo22-compare-product__badge";
      badge.textContent = "Standard-Produkt";
      generic.append(visual, badge);
      const rightList = right.querySelector("ul");
      right.insertBefore(generic, rightList);
    }

    panel.classList.add("tgo22-compare-panel");
    section.dataset.tgo22ProductsReady = "true";
    return true;
  }

  function initialiseCart(hero) {
    const button = hero?.querySelector(".tgo19-cta");
    if (!button || button.dataset.tgo19CartReady === "true") {
      return Boolean(button);
    }

    const label = button.querySelector(".tgo19-cta__label");
    const reassurance = button.querySelector(
      ".tgo19-cta__reassurance > span:last-child",
    );
    const originalLabel = label?.textContent.trim() || "GOLDEN ROUTINE STARTEN";
    const originalReassurance =
      reassurance?.textContent.trim() ||
      "30 Tage Geld-zurück-Garantie";

    const setState = (nextLabel, nextReassurance, busy) => {
      if (label) label.textContent = nextLabel;
      if (reassurance) reassurance.textContent = nextReassurance;
      button.disabled = busy;
      button.toggleAttribute("aria-busy", busy);
    };

    const resetState = () => {
      setState(
        button.dataset.tgo20Label || originalLabel,
        originalReassurance,
        false,
      );
    };

    const refreshCartUi = async () => {
      const root = window.Shopify?.routes?.root || "/";
      const [drawerResponse, cartResponse] = await Promise.all([
        window.fetch(`${root}cart?section_id=cart-drawer`, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
        }),
        window.fetch(`${root}cart.js`, {
          headers: { Accept: "application/json" },
        }),
      ]);
      if (!drawerResponse.ok || !cartResponse.ok) {
        throw new Error("Cart refresh failed");
      }

      const [drawerMarkup, cart] = await Promise.all([
        drawerResponse.text(),
        cartResponse.json(),
      ]);
      const documentFragment = new DOMParser().parseFromString(
        drawerMarkup,
        "text/html",
      );
      const nextDrawer = documentFragment.querySelector("cart-drawer");
      const currentDrawer = document.querySelector("cart-drawer");

      if (currentDrawer && nextDrawer) {
        currentDrawer.innerHTML = nextDrawer.innerHTML;
        currentDrawer.classList.remove("is-empty");
        currentDrawer.classList.add("active");
        currentDrawer
          .querySelector(".drawer__inner")
          ?.setAttribute("tabindex", "-1");
      }

      const count = Number(cart.item_count || 0);
      const bubble = document.querySelector(".cart-count-bubble");
      if (bubble) {
        const visible = bubble.querySelector('span[aria-hidden="true"]');
        const accessible = bubble.querySelector(".visually-hidden");
        if (visible) visible.textContent = String(count);
        if (accessible) {
          accessible.textContent = `${count} ${count === 1 ? "Artikel" : "Artikel"}`;
        }
        bubble.hidden = count < 1;
      }

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { cart, source: "the-golden-one" },
        }),
      );
      return cart;
    };

    let pendingAdd = null;

    // Single add-to-cart flow for the whole page. The payload (variant,
    // selling plan, quantity — e.g. 3 cans for the 12-week routine) is
    // built from the hidden source card that is currently ".selected".
    const performAdd = ({ updateButton = true } = {}) => {
      if (pendingAdd) return pendingAdd;

      const selection = readSelection(hero);
      const item = buildCartItem(selection);
      if (!item) {
        if (updateButton) {
          setState(
            "AUSWAHL PRÜFEN",
            "Bitte wähle zuerst deine Routine",
            true,
          );
          window.setTimeout(resetState, 1800);
        }
        return Promise.reject(
          new Error("The Golden One: no valid purchase selection"),
        );
      }

      if (updateButton) {
        setState(
          "WIRD HINZUGEFÜGT …",
          "Deine Routine wird vorbereitet",
          true,
        );
      }

      pendingAdd = (async () => {
        try {
          const kitItem = await buildWelcomeKitItem(hero, selection);
          // If adding with the kit fails (e.g. the kit is sold out), retry
          // without it — the kit must never block the subscription itself.
          // The kit goes FIRST in the payload: the cart lists lines newest
          // first, so the subscription renders above and the kit below it.
          const attempts = kitItem ? [[kitItem, item], [item]] : [[item]];

          let lastError = null;
          for (const items of attempts) {
            const response = await window.fetch(
              `${window.Shopify?.routes?.root || "/"}cart/add.js`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({ items }),
              },
            );
            const result = await response.json();
            if (response.ok && !result.status) {
              lastError = null;
              break;
            }
            lastError = new Error(result.description || "Cart request failed");
          }
          if (lastError) throw lastError;

          const cart = await refreshCartUi();

          if (updateButton) {
            setState("IM WARENKORB", "Deine Routine wurde hinzugefügt", true);
            window.setTimeout(resetState, 1600);
          }
          return cart;
        } catch (error) {
          console.error("The Golden One cart request failed", error);
          if (updateButton) {
            setState(
              "ERNEUT VERSUCHEN",
              "Hinzufügen war nicht möglich",
              true,
            );
            window.setTimeout(resetState, 2200);
          }
          throw error;
        } finally {
          pendingAdd = null;
        }
      })();

      return pendingAdd;
    };

    document.addEventListener(
      "click",
      (event) => {
        const clickedButton = event.target.closest(".tgo19-cta");
        if (clickedButton !== button) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (button.disabled) return;
        performAdd().catch(() => {});
      },
      true,
    );

    // Expose the shared flow to the sticky bar and the bottom CTA.
    purchaseState.add = (options = {}) =>
      performAdd({ updateButton: false, ...options });

    button.dataset.tgo19CartReady = "true";
    announcePurchaseReady();
    return true;
  }

  function initialiseAthletes() {
    const root = document.querySelector(".tgo19-athletes");
    const track = root?.querySelector(".tgo19-athletes__track");
    if (
      !root ||
      !track ||
      root.dataset.tgo19AthletesReady === "true"
    ) {
      return Boolean(root && track);
    }

    const previous = root.querySelector("[data-tgo-prev]");
    const next = root.querySelector("[data-tgo-next]");
    const progress = root.querySelector("[data-tgo-progress]");
    const cards = [...track.querySelectorAll(".tgo19-athlete-card")];
    const maximum = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const step = () => {
      const first = cards[0];
      if (!first) return Math.max(280, track.clientWidth * 0.8);
      const style = window.getComputedStyle(track);
      const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const max = maximum();
      const atStart = max <= 2 || track.scrollLeft <= 2;
      const atEnd = max <= 2 || track.scrollLeft >= max - 2;
      if (previous) {
        previous.disabled = atStart;
        previous.setAttribute("aria-disabled", String(atStart));
      }
      if (next) {
        next.disabled = atEnd;
        next.setAttribute("aria-disabled", String(atEnd));
      }
      if (progress) {
        const visible =
          track.scrollWidth > 0
            ? Math.min(100, (track.clientWidth / track.scrollWidth) * 100)
            : 100;
        const left =
          max > 0
            ? (track.scrollLeft / max) * Math.max(0, 100 - visible)
            : 0;
        progress.style.width = `${visible}%`;
        progress.style.left = `${left}%`;
      }
    };

    const move = (direction) => {
      const target = Math.max(
        0,
        Math.min(maximum(), track.scrollLeft + direction * step()),
      );
      track.scrollLeft = target;
      window.requestAnimationFrame(update);
      window.setTimeout(update, 380);
    };

    previous?.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        move(-1);
      },
      true,
    );
    next?.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        move(1);
      },
      true,
    );
    track.addEventListener("scroll", update, { passive: true });

    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragging = false;
    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      dragging = true;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      track.classList.add("is-dragging");
      track.setPointerCapture(event.pointerId);
      event.preventDefault();
    });
    track.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      track.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
    });
    const stopDragging = (event) => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove("is-dragging");
      if (track.hasPointerCapture(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
      update();
    };
    track.addEventListener("pointerup", stopDragging);
    track.addEventListener("pointercancel", stopDragging);
    cards.forEach((card) => {
      card.querySelectorAll("img").forEach((image) => {
        image.draggable = false;
      });
    });
    window.addEventListener("resize", update, { passive: true });
    window.requestAnimationFrame(update);

    root.dataset.tgo19AthletesReady = "true";
    return true;
  }

  function initialise() {
    const hero = document.querySelector(HERO_SELECTOR);
    if (!hero) return;

    purchaseState.hero = hero;
    enforceSectionOrder(hero);
    cleanHero(hero);
    initialiseGallery(hero);
    const plansReady = stabilisePlans(hero);
    const purchaseReady = initialiseDynamicPurchase(hero);
    positionPurchaseProof(hero);
    const videoReady = stabiliseVideo(hero);
    const videoPlaced = positionVideoUnderGallery(hero);
    const cartReady = initialiseCart(hero);
    const athletesReady = initialiseAthletes();
    const comparisonReady = initialiseComparisonProducts();

    if (
      !plansReady ||
      !purchaseReady ||
      !videoReady ||
      !videoPlaced ||
      !cartReady ||
      !athletesReady ||
      !comparisonReady ||
      typeof window.Swiper === "undefined"
    ) {
      let attempts = 0;
      const retry = window.setInterval(() => {
        attempts += 1;
        enforceSectionOrder(hero);
        cleanHero(hero);
        const galleryReady = initialiseGallery(hero);
        const nextPlansReady = stabilisePlans(hero);
        const nextPurchaseReady = initialiseDynamicPurchase(hero);
        positionPurchaseProof(hero);
        const nextVideoReady = stabiliseVideo(hero);
        const nextVideoPlaced = positionVideoUnderGallery(hero);
        const nextCartReady = initialiseCart(hero);
        const nextAthletesReady = initialiseAthletes();
        const nextComparisonReady = initialiseComparisonProducts();
        if (
          (galleryReady ||
            hero
              .querySelector("[data-tgo19-gallery]")
              ?.dataset.tgo19GalleryReady === "true") &&
          nextPlansReady &&
          nextPurchaseReady &&
          nextVideoReady &&
          nextVideoPlaced &&
          nextCartReady &&
          nextAthletesReady &&
          nextComparisonReady
        ) {
          window.clearInterval(retry);
        }
        if (attempts >= 20) window.clearInterval(retry);
      }, 150);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialise, { once: true });
  } else {
    initialise();
  }

  document.addEventListener("shopify:section:load", initialise);
})();

/* Golden One PDP · narrow-screen editorial layout guard */
(() => {
  const media = window.matchMedia('(max-width: 749px)');
  const rootSelector = 'body:has(.tgo19-hero)';
  const important = (element, property, value) => {
    if (element) element.style.setProperty(property, value, 'important');
  };
  const clear = (element, properties) => {
    if (!element) return;
    properties.forEach((property) => element.style.removeProperty(property));
  };

  const syncEditorialMobile = () => {
    const root = document.querySelector(rootSelector);
    if (!root) return;

    const journeyTrack = root.querySelector('.tgo13-journey__track');
    const journeyCards = [...root.querySelectorAll('.tgo13-journey-card')];
    const comparePanel = root.querySelector('.tgo13-compare__panel.tgo22-compare-panel');
    const compareColumns = [...root.querySelectorAll('.tgo13-compare__column')];

    if (media.matches) {
      important(journeyTrack, 'display', 'block');
      important(journeyTrack, 'height', 'auto');
      important(journeyTrack, 'max-height', 'none');
      important(journeyTrack, 'overflow', 'visible');

      journeyCards.forEach((card) => {
        important(card, 'display', 'grid');
        important(card, 'grid-template-columns', '44px minmax(0, 1fr)');
        important(card, 'grid-template-rows', 'auto auto auto');
        important(card, 'width', '100%');
        important(card, 'min-width', '0');
        important(card, 'height', 'auto');
        important(card, 'min-height', '0');
        important(card, 'max-height', 'none');
      });

      important(comparePanel, 'display', 'flex');
      important(comparePanel, 'grid-template-columns', 'none');
      compareColumns.forEach((column) => {
        important(column, 'flex', '0 0 50%');
        important(column, 'width', '50%');
        important(column, 'min-width', '50%');
        important(column, 'max-width', '50%');
      });
      return;
    }

    clear(journeyTrack, ['display', 'height', 'max-height', 'overflow']);
    journeyCards.forEach((card) => {
      clear(card, [
        'display',
        'grid-template-columns',
        'grid-template-rows',
        'width',
        'min-width',
        'height',
        'min-height',
        'max-height'
      ]);
    });
    clear(comparePanel, ['display', 'grid-template-columns']);
    compareColumns.forEach((column) => {
      clear(column, ['flex', 'width', 'min-width', 'max-width']);
    });
  };

  const start = () => {
    if (document.documentElement.dataset.tgoEditorialGuard === 'ready') return;
    document.documentElement.dataset.tgoEditorialGuard = 'ready';
    syncEditorialMobile();
    media.addEventListener?.('change', syncEditorialMobile);
    window.addEventListener('shopify:section:load', syncEditorialMobile);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* Golden One PDP · final mobile component geometry */
(() => {
  const media = window.matchMedia('(max-width: 749px)');
  const set = (element, property, value) => {
    if (element) element.style.setProperty(property, value, 'important');
  };
  const unset = (element, properties) => {
    if (!element) return;
    properties.forEach((property) => element.style.removeProperty(property));
  };

  const sync = () => {
    const root = document.querySelector('body:has(.tgo19-hero)');
    if (!root) return;

    const video = root.querySelector('.tgo19-video');
    const videoMedia = root.querySelector('.tgo19-video__media');
    const videoBody = root.querySelector('.tgo19-video__body');
    const comparePanel = root.querySelector('.tgo13-compare__panel');
    const compareColumns = [...root.querySelectorAll('.tgo13-compare__column')];
    const compareLabels = [...root.querySelectorAll('.tgo13-compare__label')];
    const compareProducts = [...root.querySelectorAll('.tgo13-compare__product, .tgo22-compare-product')];
    const compareItems = [...root.querySelectorAll('.tgo13-compare__column > ul > li')];
    const compareCopies = [...root.querySelectorAll('.tgo13-compare__feature-copy')];
    const goldenImage = root.querySelector('.tgo13-compare__product-image');

    if (media.matches) {
      set(video, 'display', 'block');
      set(video, 'height', '116px');
      set(video, 'min-height', '116px');
      set(video, 'max-height', '116px');
      set(videoMedia, 'position', 'absolute');
      set(videoMedia, 'inset', '0 auto 0 0');
      set(videoMedia, 'width', '39%');
      set(videoMedia, 'height', '116px');
      set(videoMedia, 'z-index', '1');
      set(videoBody, 'position', 'absolute');
      set(videoBody, 'inset', '0 0 0 39%');
      set(videoBody, 'display', 'flex');
      set(videoBody, 'width', 'auto');
      set(videoBody, 'height', '116px');
      set(videoBody, 'z-index', '2');

      set(comparePanel, 'display', 'flex');
      set(comparePanel, 'width', '100%');
      compareColumns.forEach((column) => {
        set(column, 'display', 'flex');
        set(column, 'flex', '0 0 50%');
        set(column, 'width', '50%');
        set(column, 'min-width', '50%');
        set(column, 'max-width', '50%');
        set(column, 'padding', '24px 12px 18px');
      });
      if (compareColumns[0]) set(compareColumns[0], 'background', '#fff');
      compareLabels.forEach((label) => {
        set(label, 'width', '100%');
        set(label, 'min-width', '0');
        set(label, 'word-break', 'normal');
        set(label, 'overflow-wrap', 'normal');
      });
      compareProducts.forEach((product) => {
        set(product, 'width', '100%');
        set(product, 'min-width', '0');
      });
      compareItems.forEach((item) => {
        set(item, 'display', 'grid');
        set(item, 'grid-template-columns', '20px minmax(0, 1fr)');
        set(item, 'width', '100%');
        set(item, 'min-width', '0');
      });
      compareCopies.forEach((copy) => {
        set(copy, 'display', 'grid');
        set(copy, 'width', 'auto');
        set(copy, 'min-width', '0');
        set(copy, 'word-break', 'normal');
        set(copy, 'overflow-wrap', 'anywhere');
      });
      set(goldenImage, 'width', '138px');
      set(goldenImage, 'height', '138px');
      return;
    }

    unset(video, ['display', 'height', 'min-height', 'max-height']);
    unset(videoMedia, ['position', 'inset', 'width', 'height', 'z-index']);
    unset(videoBody, ['position', 'inset', 'display', 'width', 'height', 'z-index']);
    unset(comparePanel, ['display', 'width']);
    compareColumns.forEach((column) => {
      unset(column, ['display', 'flex', 'width', 'min-width', 'max-width', 'padding', 'background']);
    });
    compareLabels.forEach((label) => unset(label, ['width', 'min-width', 'word-break', 'overflow-wrap']));
    compareProducts.forEach((product) => unset(product, ['width', 'min-width']));
    compareItems.forEach((item) => unset(item, ['display', 'grid-template-columns', 'width', 'min-width']));
    compareCopies.forEach((copy) => unset(copy, ['display', 'width', 'min-width', 'word-break', 'overflow-wrap']));
    unset(goldenImage, ['width', 'height']);
  };

  const start = () => {
    if (document.documentElement.dataset.tgoMobileGeometry === 'ready') return;
    document.documentElement.dataset.tgoMobileGeometry = 'ready';
    sync();
    media.addEventListener?.('change', sync);
    window.addEventListener('shopify:section:load', sync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* Golden One PDP · semantic mobile comparison table */
(() => {
  const buildMobileComparison = () => {
    const section = document.querySelector('body:has(.tgo19-hero) .tgo13-compare');
    if (!section || section.querySelector('.tgo35-mobile-compare')) return;

    const panel = section.querySelector('.tgo13-compare__panel');
    const goldenColumn = section.querySelector('.tgo13-compare__column--dark');
    const otherColumn = section.querySelector('.tgo13-compare__column--light');
    const productImage = goldenColumn?.querySelector('.tgo13-compare__product-image');
    const goldenRows = [...(goldenColumn?.querySelectorAll(':scope > ul > li') || [])];
    const otherRows = [...(otherColumn?.querySelectorAll(':scope > ul > li') || [])];
    if (!panel || !productImage || !goldenRows.length) return;

    const table = document.createElement('div');
    table.className = 'tgo35-mobile-compare';
    table.setAttribute('aria-label', 'The Golden One im direkten Nährstoffvergleich');

    const head = document.createElement('div');
    head.className = 'tgo35-mobile-compare__head';

    const corner = document.createElement('span');
    corner.className = 'tgo35-mobile-compare__corner';
    corner.textContent = 'Nährstoff';

    const goldenHead = document.createElement('div');
    goldenHead.className = 'tgo35-mobile-compare__product is-golden';
    const image = productImage.cloneNode(true);
    image.removeAttribute('loading');
    const goldenTitle = document.createElement('strong');
    goldenTitle.textContent = 'The Golden One';
    const goldenBadge = document.createElement('span');
    goldenBadge.textContent = '55 Inhaltsstoffe';
    goldenHead.append(image, goldenTitle, goldenBadge);

    const otherHead = document.createElement('div');
    otherHead.className = 'tgo35-mobile-compare__product is-other';
    const otherTitle = document.createElement('strong');
    otherTitle.textContent = 'Green Powder';
    const otherBadge = document.createElement('span');
    otherBadge.textContent = 'Standard';
    otherHead.append(otherTitle, otherBadge);

    head.append(corner, goldenHead, otherHead);
    table.append(head);

    goldenRows.forEach((goldenRow, index) => {
      const otherRow = otherRows[index];
      const feature = goldenRow.querySelector('.tgo13-compare__feature-copy b')?.textContent?.trim();
      const goldenValue = goldenRow.querySelector('.tgo13-compare__feature-copy small')?.textContent?.trim();
      const otherValue = otherRow?.querySelector('.tgo13-compare__feature-copy small')?.textContent?.trim();
      if (!feature) return;

      const row = document.createElement('div');
      row.className = 'tgo35-mobile-compare__row';

      const name = document.createElement('strong');
      name.className = 'tgo35-mobile-compare__name';
      name.textContent = feature;

      const yes = document.createElement('span');
      yes.className = 'tgo35-mobile-compare__value is-golden';
      const yesIcon = goldenRow.querySelector('svg')?.cloneNode(true);
      if (yesIcon) yes.append(yesIcon);
      const yesText = document.createElement('span');
      yesText.textContent = goldenValue || 'enthalten';
      yes.append(yesText);

      const no = document.createElement('span');
      no.className = 'tgo35-mobile-compare__value is-other';
      const noIcon = otherRow?.querySelector('svg')?.cloneNode(true);
      if (noIcon) no.append(noIcon);
      const noText = document.createElement('span');
      noText.textContent = otherValue || 'nicht vorhanden';
      no.append(noText);

      row.append(name, yes, no);
      table.append(row);
    });

    panel.before(table);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildMobileComparison, { once: true });
  } else {
    buildMobileComparison();
  }
  window.addEventListener('shopify:section:load', buildMobileComparison);
})();

/* Golden One PDP · activate semantic comparison on mobile */
(() => {
  const media = window.matchMedia('(max-width: 749px)');
  const sync = () => {
    const section = document.querySelector('body:has(.tgo19-hero) .tgo13-compare');
    const panel = section?.querySelector('.tgo13-compare__panel');
    const table = section?.querySelector('.tgo35-mobile-compare');
    if (!panel || !table) return;
    if (media.matches) {
      panel.style.setProperty('display', 'none', 'important');
      table.style.setProperty('display', 'block', 'important');
    } else {
      panel.style.removeProperty('display');
      table.style.removeProperty('display');
    }
  };
  const start = () => {
    requestAnimationFrame(sync);
    window.setTimeout(sync, 120);
    media.addEventListener?.('change', sync);
    window.addEventListener('shopify:section:load', sync);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* Golden One PDP · final teaser legibility */
(() => {
  const media = window.matchMedia('(max-width: 749px)');
  const sync = () => {
    const root = document.querySelector('body:has(.tgo19-hero)');
    const description = root?.querySelector('.tgo19-video__description');
    const play = root?.querySelector('.tgo19-video__play');
    const playIcon = play?.querySelector('img');
    if (!description || !play) return;
    if (media.matches) {
      description.style.setProperty('font-size', '9.6px', 'important');
      description.style.setProperty('letter-spacing', '-0.01em', 'important');
      description.style.setProperty('text-overflow', 'clip', 'important');
      play.style.setProperty('width', '38px', 'important');
      play.style.setProperty('height', '38px', 'important');
      play.style.setProperty('border', '0', 'important');
      play.style.setProperty('background', '#ab8c44', 'important');
      play.style.setProperty('box-shadow', 'none', 'important');
      play.style.setProperty('z-index', '3', 'important');
      playIcon?.style.setProperty('filter', 'brightness(0) invert(1)', 'important');
    } else {
      ['font-size', 'letter-spacing', 'text-overflow'].forEach((property) => description.style.removeProperty(property));
      ['width', 'height', 'border', 'background', 'box-shadow', 'z-index'].forEach((property) => play.style.removeProperty(property));
      playIcon?.style.removeProperty('filter');
    }
  };
  const start = () => {
    sync();
    media.addEventListener?.('change', sync);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();


/* Golden One PDP · V43 responsive recovery guard */
(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  const set = (element, property, value) => {
    if (element) element.style.setProperty(property, value, 'important');
  };
  const unset = (element, properties) => {
    if (!element) return;
    properties.forEach((property) => element.style.removeProperty(property));
  };

  const ensureComparison = (root) => {
    const section = root.querySelector('.tgo13-compare');
    const panel = section?.querySelector('.tgo13-compare__panel');
    const table = section?.querySelector('.tgo35-mobile-compare');
    if (!section || !panel || !table) return;

    const otherHead = table.querySelector('.tgo35-mobile-compare__product.is-other');
    const otherSource = panel.querySelector(
      '.tgo13-compare__column--light .tgo13-compare__product-image'
    );
    if (otherHead && otherSource && !otherHead.querySelector('img')) {
      const image = otherSource.cloneNode(true);
      image.removeAttribute('loading');
      otherHead.prepend(image);
    }

    set(panel, 'display', 'none');
    set(table, 'display', 'block');
  };

  const sync = () => {
    const root = document.querySelector('body:has(.tgo19-hero)');
    if (!root) return;

    ensureComparison(root);

    const timer = root.querySelector('.tgo19-video__timer');
    const duration = root.querySelector('.tgo19-video__duration');
    set(timer, 'display', 'none');
    set(duration, 'display', 'inline-flex');

    const journeyTrack = root.querySelector('.tgo13-journey__track');
    const journeyCards = [...root.querySelectorAll('.tgo13-journey-card')];

    if (!mobile.matches) {
      unset(journeyTrack, [
        'display',
        'grid-auto-flow',
        'grid-auto-columns',
        'grid-template-columns',
        'gap',
        'width',
        'height',
        'max-height',
        'overflow',
        'overflow-x',
        'overflow-y'
      ]);
      journeyCards.forEach((card) => {
        unset(card, [
          'display',
          'grid-template-columns',
          'grid-template-rows',
          'width',
          'min-width',
          'height',
          'min-height',
          'max-height',
          'flex-direction'
        ]);
      });
      return;
    }

    set(journeyTrack, 'display', 'grid');
    set(journeyTrack, 'grid-auto-flow', 'column');
    set(journeyTrack, 'grid-auto-columns', 'min(82vw, 350px)');
    set(journeyTrack, 'grid-template-columns', 'none');
    set(journeyTrack, 'gap', '14px');
    set(journeyTrack, 'width', '100%');
    set(journeyTrack, 'height', 'auto');
    set(journeyTrack, 'max-height', 'none');
    set(journeyTrack, 'overflow', 'auto hidden');
    set(journeyTrack, 'overflow-x', 'auto');
    set(journeyTrack, 'overflow-y', 'hidden');

    journeyCards.forEach((card) => {
      set(card, 'display', 'flex');
      set(card, 'grid-template-columns', 'none');
      set(card, 'grid-template-rows', 'none');
      set(card, 'width', 'auto');
      set(card, 'min-width', '0');
      set(card, 'height', 'auto');
      set(card, 'min-height', '380px');
      set(card, 'max-height', 'none');
      set(card, 'flex-direction', 'column');
    });
  };

  const start = () => {
    sync();
    requestAnimationFrame(sync);
    window.setTimeout(sync, 180);
    mobile.addEventListener?.('change', sync);
    window.addEventListener('resize', sync, { passive: true });
    window.addEventListener('shopify:section:load', sync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();


/* Golden One PDP · V43 competitor asset handoff */
(() => {
  const attachCompetitorAsset = () => {
    const section = document.querySelector('body:has(.tgo19-hero) .tgo13-compare');
    const otherHead = section?.querySelector(
      '.tgo35-mobile-compare__product.is-other'
    );
    const source = section?.querySelector('.tgo22-compare-product__visual');
    if (!otherHead || !source || otherHead.querySelector('img')) return;

    const background = window.getComputedStyle(source).backgroundImage;
    const match = background.match(/^url\\([\"']?(.*?)[\"']?\\)$/);
    if (!match?.[1]) return;

    const image = document.createElement('img');
    image.src = match[1];
    image.alt = '';
    image.width = 512;
    image.height = 512;
    image.setAttribute('aria-hidden', 'true');
    otherHead.prepend(image);
  };

  const start = () => {
    attachCompetitorAsset();
    requestAnimationFrame(attachCompetitorAsset);
    window.setTimeout(attachCompetitorAsset, 180);
    window.addEventListener('shopify:section:load', attachCompetitorAsset);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();


/* TGO PDP V46 — resilient visual guard for legacy inline overrides */
(() => {
  'use strict';

  const isTgo = () => Boolean(document.querySelector('.tgo13-hero--golden-one, .golden-smartrr.tgo19-hero'));
  const important = (node, name, value) => node?.style.setProperty(name, value, 'important');

  function restoreCompetitorProduct() {
    const target = document.querySelector('.tgo35-mobile-compare__product.is-other');
    if (!target || target.querySelector('.tgo46-compare-other__image')) return;

    const source = document.querySelector('.tgo22-compare-product__visual');
    if (!source) return;
    const background = getComputedStyle(source).backgroundImage;
    const match = background.match(/^url\(["']?(.*?)["']?\)$/);
    if (!match?.[1]) return;

    const image = document.createElement('img');
    image.className = 'tgo46-compare-other__image';
    image.src = match[1];
    image.alt = 'Typisches Standard Green Powder';
    image.width = 180;
    image.height = 160;
    target.prepend(image);
  }

  function enforceMobileJourney() {
    if (!matchMedia('(max-width: 749px)').matches) return;
    const track = document.querySelector('.tgo13-journey__track');
    if (!track) return;

    important(track, 'display', 'grid');
    important(track, 'grid-auto-flow', 'column');
    important(track, 'grid-template-columns', 'none');
    important(track, 'grid-auto-columns', 'min(82vw, 314px)');
    important(track, 'gap', '14px');
    important(track, 'overflow-x', 'auto');
    important(track, 'overflow-y', 'hidden');
    important(track, 'scroll-snap-type', 'x mandatory');

    track.querySelectorAll('.tgo13-journey-card').forEach((card) => {
      important(card, 'display', 'flex');
      important(card, 'flex-direction', 'column');
      important(card, 'width', 'auto');
      important(card, 'min-width', '0');
      important(card, 'min-height', '398px');
      important(card, 'padding', '24px 22px 26px');
      important(card, 'overflow', 'hidden');
      important(card, 'border', '0');
      important(card, 'border-top', '3px solid rgba(181, 141, 58, .72)');
      important(card, 'border-radius', '12px');
      important(card, 'background', '#fff');
      important(card, 'box-shadow', '0 16px 38px rgba(28, 24, 18, .07)');
      important(card, 'scroll-snap-align', 'start');
    });
  }

  function apply() {
    if (!isTgo()) return;
    restoreCompetitorProduct();
    enforceMobileJourney();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply, { once: true });
  } else {
    apply();
  }
  window.addEventListener('load', apply, { once: true });
  window.addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 220);
  setTimeout(apply, 900);
})();


/* TGO PDP V46b — correct legacy mobile inline-important declarations */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');

  function applyMobileCleanup() {
    if (!document.querySelector('.tgo13-hero--golden-one, .golden-smartrr.tgo19-hero')) return;
    if (!matchMedia('(max-width: 749px)').matches) return;

    set(document.querySelector('.tgo13-proof-strip'), 'display', 'none');
    set(document.querySelector('.golden-text-image-trust__trust-bar--mobile-only'), 'display', 'none');

    const heading = document.querySelector('.tgo13-journey .tgo13-heading');
    set(heading, 'text-align', 'center');
    set(heading?.querySelector('h2'), 'text-align', 'center');
    set(heading?.querySelector('p'), 'text-align', 'center');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyMobileCleanup, { once: true });
  } else {
    applyMobileCleanup();
  }
  window.addEventListener('load', applyMobileCleanup, { once: true });
  window.addEventListener('resize', applyMobileCleanup, { passive: true });
  setTimeout(applyMobileCleanup, 250);
  setTimeout(applyMobileCleanup, 950);
})();


/* TGO PDP V47 — enforce the final editorial card geometry across legacy layers */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');

  function tuneTargetCards() {
    document.querySelectorAll('.tgo13-target-card').forEach((card) => {
      set(card, 'display', 'grid');
      set(card, 'grid-template-rows', 'auto 1fr');
      set(card, 'min-width', '0');

      const media = card.querySelector('.tgo13-target-card__media');
      set(media, 'aspect-ratio', '1.22 / 1');
      set(media, 'min-height', '0');
      set(media, 'overflow', 'hidden');
      set(media, 'border-radius', '14px');

      const body = card.querySelector('.tgo13-target-card__body');
      set(body, 'position', 'relative');
      set(body, 'display', 'grid');
      set(body, 'align-content', 'start');
      set(body, 'gap', '0');
      set(body, 'padding', '26px 4px 0');

      const icon = card.querySelector('.tgo13-target-card__icon');
      set(icon, 'position', 'absolute');
      set(icon, 'top', '-25px');
      set(icon, 'left', '16px');
      set(icon, 'width', '50px');
      set(icon, 'height', '50px');
      set(icon, 'margin', '0');
      set(icon, 'border', '1px solid rgba(181, 141, 58, .72)');
      set(icon, 'border-radius', '50%');
      set(icon, 'background', '#fff');
      set(icon, 'box-shadow', 'none');
    });
  }

  function tuneJourney() {
    const track = document.querySelector('.tgo13-journey__track');
    if (!track) return;
    const mobile = matchMedia('(max-width: 749px)').matches;

    if (!mobile) {
      set(track, 'display', 'grid');
      set(track, 'grid-template-columns', 'repeat(5, minmax(0, 1fr))');
      set(track, 'grid-auto-flow', 'row');
      set(track, 'gap', '14px');
      set(track, 'overflow', 'visible');
      set(track, 'padding', '0');
    }

    track.querySelectorAll('.tgo13-journey-card').forEach((card) => {
      set(card, 'position', 'relative');
      set(card, 'display', 'flex');
      set(card, 'flex-direction', 'column');
      set(card, 'width', 'auto');
      set(card, 'min-width', '0');
      set(card, 'min-height', mobile ? '398px' : '390px');
      set(card, 'padding', mobile ? '24px 22px 26px' : '26px 24px 28px');
      set(card, 'overflow', 'hidden');
      set(card, 'border', '0');
      set(card, 'border-top', '3px solid rgba(181, 141, 58, .72)');
      set(card, 'border-radius', '12px');
      set(card, 'background', '#fff');
      set(card, 'box-shadow', '0 16px 38px rgba(28, 24, 18, .07)');
      if (mobile) set(card, 'scroll-snap-align', 'start');
    });
  }

  function apply() {
    if (!document.querySelector('.tgo13-hero--golden-one, .golden-smartrr.tgo19-hero')) return;
    tuneTargetCards();
    tuneJourney();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply, { once: true });
  } else {
    apply();
  }
  window.addEventListener('load', apply, { once: true });
  window.addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 250);
  setTimeout(apply, 950);
})();


/* TGO PDP V48 — keep target-card copy on a full, readable measure */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');

  function apply() {
    if (!document.querySelector('.tgo13-hero--golden-one, .golden-smartrr.tgo19-hero')) return;
    document.querySelectorAll('.tgo13-target-card').forEach((card) => {
      const body = card.querySelector('.tgo13-target-card__body');
      set(body, 'width', '100%');
      set(body, 'max-width', 'none');
      set(body, 'justify-self', 'stretch');
      set(body, 'box-sizing', 'border-box');
      set(body?.querySelector('h3'), 'width', '100%');
      set(body?.querySelector('ul'), 'width', '100%');
      body?.querySelectorAll('li').forEach((item) => {
        set(item, 'display', 'block');
        set(item, 'width', '100%');
        set(item, 'box-sizing', 'border-box');
        set(item, 'padding-left', '27px');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply, { once: true });
  } else {
    apply();
  }
  window.addEventListener('load', apply, { once: true });
  window.addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 250);
  setTimeout(apply, 950);
})();

/* TGO PDP V50 — mobile guard for legacy inline-important geometry */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');

  function addDetailHeader() {
    if (!matchMedia('(max-width: 749px)').matches) return;
    const grid = document.querySelector('.tgo13-compare__more-grid');
    if (!grid || grid.querySelector('.tgo50-detail-head')) return;
    const head = document.createElement('div');
    head.className = 'tgo50-detail-head';
    ['Nährstoff', 'The Golden One', 'Green Powder'].forEach((label) => {
      const cell = document.createElement('span');
      cell.textContent = label;
      head.appendChild(cell);
    });
    grid.prepend(head);
  }

  function clarifyPurchaseCopy() {
    if (!matchMedia('(max-width: 749px)').matches) return;
    const comparison = document.querySelector('.tgo20-purchase__comparison');
    comparison?.querySelector('.tgo20-purchase__comparison-main > p:first-child strong')?.replaceChildren('Dein Preis');
    comparison?.querySelector('.tgo20-purchase__saving > span')?.replaceChildren('Du sparst');
  }

  function enforceMobileGeometry() {
    if (!matchMedia('(max-width: 749px)').matches) return;

    const stage = document.querySelector('.tgo19-gallery__stage');
    set(stage, 'height', 'auto');
    set(stage, 'min-height', '0');
    set(stage, 'aspect-ratio', '1 / 1');
    set(stage, 'overflow', 'hidden');
    [
      document.querySelector('.tgo19-gallery__swiper'),
      document.querySelector('.tgo19-gallery__swiper .swiper-wrapper'),
      ...document.querySelectorAll('.tgo19-gallery__slide'),
    ].filter(Boolean).forEach((node) => {
      set(node, 'height', 'auto');
      set(node, 'min-height', '0');
      set(node, 'aspect-ratio', '1 / 1');
    });
    document.querySelectorAll('.tgo19-gallery__slide img').forEach((image) => {
      set(image, 'width', '100%');
      set(image, 'height', '100%');
      set(image, 'max-width', '100%');
      set(image, 'object-fit', 'contain');
      set(image, 'object-position', 'center');
    });

    const guarantee = document.querySelector('.tgo20-purchase__guarantee');
    set(guarantee, 'display', 'flex');

    const paymentIcons = document.querySelector('.golden-smartrr__payment-icons');
    set(paymentIcons, 'width', '100%');
    set(paymentIcons, 'max-width', 'none');
    set(paymentIcons, 'overflow', 'visible');

    document.querySelectorAll('.tgo13-target-card__icon').forEach((icon) => {
      set(icon, 'width', '54px');
      set(icon, 'height', '54px');
      set(icon, 'top', '-27px');
      set(icon, 'border', '0');
      set(icon, 'background', '#b58d3a');
      set(icon, 'color', '#fff');
      set(icon, 'box-shadow', 'none');
    });

    const whyImage = document.querySelector('.tgo13-why__product img');
    set(whyImage, 'width', '100%');
    set(whyImage, 'height', '100%');
    set(whyImage, 'max-width', '100%');
    set(whyImage, 'padding', '22px 20px 0');
    set(whyImage, 'box-sizing', 'border-box');
    set(whyImage, 'object-fit', 'contain');
    set(whyImage, 'object-position', 'center');
    set(whyImage, 'transform', 'none');

    const compareImage = document.querySelector('.tgo35-mobile-compare__product.is-golden img');
    set(compareImage, 'width', 'calc(100% - 18px)');
    set(compareImage, 'height', '122px');
    set(compareImage, 'max-width', '120px');
    set(compareImage, 'padding', '8px');
    set(compareImage, 'box-sizing', 'border-box');
    set(compareImage, 'background', 'transparent');
    set(compareImage, 'object-fit', 'contain');
    set(compareImage, 'mix-blend-mode', 'multiply');
  }

  function apply() {
    if (!document.querySelector('.tgo13-hero--golden-one, .golden-smartrr.tgo19-hero')) return;
    addDetailHeader();
    clarifyPurchaseCopy();
    enforceMobileGeometry();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply, { once: true });
  } else {
    apply();
  }
  window.addEventListener('load', apply, { once: true });
  window.addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 250);
  setTimeout(apply, 950);
})();

/* TGO PDP V50b — fixed-height media guard */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');
  function apply() {
    if (!matchMedia('(max-width: 749px)').matches) return;
    const stageHeight = `${Math.min(innerWidth - 32, 454)}px`;
    [
      document.querySelector('.tgo19-gallery__stage'),
      document.querySelector('.tgo19-gallery__swiper'),
      document.querySelector('.tgo19-gallery__swiper .swiper-wrapper'),
      ...document.querySelectorAll('.tgo19-gallery__slide'),
    ].filter(Boolean).forEach((node) => {
      set(node, 'height', stageHeight);
      set(node, 'aspect-ratio', 'auto');
    });
    const why = document.querySelector('.tgo13-why__product');
    set(why, 'position', 'relative');
    const image = why?.querySelector('img');
    set(image, 'position', 'absolute');
    set(image, 'inset', '0');
    set(image, 'width', '100%');
    set(image, 'height', '100%');
    set(image, 'max-height', '100%');
  }
  apply();
  addEventListener('load', apply, { once: true });
  addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 300);
  setTimeout(apply, 1200);
})();

/* TGO PDP V50c — release fixed gallery max-height */
(() => {
  'use strict';
  function apply() {
    if (!matchMedia('(max-width: 749px)').matches) return;
    [
      document.querySelector('.tgo19-gallery__stage'),
      document.querySelector('.tgo19-gallery__swiper'),
      document.querySelector('.tgo19-gallery__swiper .swiper-wrapper'),
      ...document.querySelectorAll('.tgo19-gallery__slide'),
    ].filter(Boolean).forEach((node) => node.style.setProperty('max-height', 'none', 'important'));
  }
  apply();
  addEventListener('load', apply, { once: true });
  setTimeout(apply, 300);
  setTimeout(apply, 1200);
})();

/* TGO PDP V50d — remove legacy logical borders */
(() => {
  'use strict';
  function apply() {
    if (!matchMedia('(max-width: 749px)').matches) return;
    const details = document.querySelector('.tgo13-compare__more');
    ['border', 'border-top', 'border-bottom', 'border-block', 'border-block-width', 'border-block-style']
      .forEach((prop) => details?.style.setProperty(prop, prop.includes('style') ? 'none' : '0', 'important'));
    document.querySelectorAll('.tgo13-target-card__icon svg').forEach((icon) => {
      icon.style.setProperty('width', '30px', 'important');
      icon.style.setProperty('max-width', 'none', 'important');
      icon.style.setProperty('height', '30px', 'important');
      icon.style.setProperty('max-height', 'none', 'important');
    });
  }
  apply();
  addEventListener('load', apply, { once: true });
  setTimeout(apply, 500);
})();

/* TGO PDP V50e — keep all payment marks inside narrow mobile viewports */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');
  function apply() {
    if (!matchMedia('(max-width: 749px)').matches) return;
    const groups = [
      document.querySelector('.tgo20-purchase__payments'),
      document.querySelector('.golden-smartrr__payment-wrap'),
      document.querySelector('.golden-smartrr__payment-icons'),
    ].filter(Boolean);
    groups.forEach((node) => {
      set(node, 'width', '100%');
      set(node, 'max-width', 'none');
      set(node, 'overflow', 'visible');
      set(node, 'transform', 'none');
    });
    const track = document.querySelector('.golden-smartrr__payment-icons');
    set(track, 'display', 'flex');
    set(track, 'justify-content', 'space-between');
    set(track, 'gap', '2px');
    document.querySelectorAll('.golden-smartrr__payment-icon').forEach((icon) => {
      set(icon, 'width', innerWidth <= 410 ? '20px' : '25px');
      set(icon, 'height', 'auto');
      set(icon, 'flex', '0 0 auto');
    });
  }
  apply();
  addEventListener('load', apply, { once: true });
  addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 450);
  setTimeout(apply, 1200);
})();

/* TGO PDP V51 — keep the comparison control inside its module */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');

  function arrangeComparison() {
    const box = document.querySelector('.tgo35-mobile-compare');
    const details = document.querySelector('.tgo13-compare__more');
    if (box && details && details.parentElement !== box) box.appendChild(details);

    const heading = document.querySelector('.tgo13-compare .tgo13-heading');
    set(heading, 'width', 'min(100%, 880px)');
    set(heading, 'margin-left', 'auto');
    set(heading, 'margin-right', 'auto');
    set(heading, 'text-align', 'center');
    heading?.querySelectorAll('.tgo13-kicker, h2, p').forEach((node) => set(node, 'text-align', 'center'));
    const description = heading?.querySelector('p:last-child');
    set(description, 'margin-left', 'auto');
    set(description, 'margin-right', 'auto');
  }

  function normalizeTargetIcons() {
    document.querySelectorAll('.tgo13-target-card__icon').forEach((icon) => {
      set(icon, 'width', '54px');
      set(icon, 'height', '54px');
      set(icon, 'top', '-27px');
      set(icon, 'border', '0');
      set(icon, 'background', '#b58d3a');
      set(icon, 'color', '#fff');
      set(icon, 'box-shadow', 'none');
      const glyph = icon.querySelector('svg');
      set(glyph, 'width', '30px');
      set(glyph, 'max-width', 'none');
      set(glyph, 'height', '30px');
      set(glyph, 'max-height', 'none');
      set(glyph, 'color', '#fff');
    });
  }

  function apply() {
    if (!document.querySelector('.tgo13-hero--golden-one')) return;
    arrangeComparison();
    normalizeTargetIcons();
  }

  apply();
  addEventListener('load', apply, { once: true });
  addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 350);
  setTimeout(apply, 1200);
})();

/* TGO PDP V51b — stable editorial comparison width */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');
  function apply() {
    if (!document.querySelector('.tgo13-hero--golden-one')) return;
    const shell = document.querySelector('.tgo13-compare > .tgo13-shell');
    set(shell, 'width', matchMedia('(max-width: 749px)').matches ? 'calc(100% - 28px)' : 'min(calc(100% - 64px), 1080px)');
    set(shell, 'max-width', '1080px');
    set(shell, 'margin-left', 'auto');
    set(shell, 'margin-right', 'auto');
    const box = shell?.querySelector('.tgo35-mobile-compare');
    set(box, 'width', '100%');
    set(box, 'max-width', 'none');
    const grid = box?.querySelector('.tgo13-compare__more-grid');
    set(grid, 'gap', '0');
  }
  apply();
  addEventListener('load', apply, { once: true });
  addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 450);
  setTimeout(apply, 1200);
})();

/* TGO PDP V52 — Tabler Icons (MIT) for target groups and benefit checks */
(() => {
  'use strict';
  const ns = 'http://www.w3.org/2000/svg';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');
  const iconPaths = [
    '<path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"/><path d="M12 7l4.76 3.45l-1.82 5.59h-5.88l-1.82-5.59z"/><path d="M12 7v-4M16.76 10.45l3.79 -1.23M15.06 16.04l2.35 3.24M8.94 16.04l-2.35 3.24M7.24 10.45l-3.79 -1.23"/>',
    '<path d="M2 12h1M6 8v8M10 5v14M14 5v14M18 8v8M21 12h1M6 12h12"/>',
    '<path d="M13 4v4l3 3l4 -1M6 4m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0M7 21l3 -4M16 21l-2 -4l-3 -3l1 -6M6 12l2 -3l4 -1"/>',
    '<path d="M10 6h4M12 4v2M9 17v-2M15 17v-2"/><path d="M4 8h16a1 1 0 0 1 1 1v11a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1v-11a1 1 0 0 1 1 -1z"/><path d="M3 13a20 20 0 0 0 18 0"/>'
  ];

  function makeIcon(paths, className, label) {
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.classList.add(className);
    if (label) svg.dataset.icon = label;
    svg.innerHTML = paths;
    return svg;
  }

  function refineCards() {
    document.querySelectorAll('.tgo13-target-card').forEach((card, index) => {
      const medallion = card.querySelector('.tgo13-target-card__icon');
      if (medallion && !medallion.querySelector('.tgo52-target-glyph')) {
        medallion.replaceChildren(makeIcon(iconPaths[index] || iconPaths[0], 'tgo52-target-glyph', ['football', 'barbell', 'run', 'briefcase'][index]));
      }
      set(medallion, 'top', matchMedia('(max-width: 749px)').matches ? '-29px' : '-31px');
      set(medallion, 'left', '18px');
      set(medallion, 'width', matchMedia('(max-width: 749px)').matches ? '58px' : '62px');
      set(medallion, 'height', matchMedia('(max-width: 749px)').matches ? '58px' : '62px');
      set(medallion, 'display', 'grid');
      set(medallion, 'place-items', 'center');
      set(medallion, 'box-sizing', 'border-box');
      set(medallion, 'border', '1px solid rgba(171, 140, 68, .46)');
      set(medallion, 'border-radius', '50%');
      set(medallion, 'background', '#fbf8f1');
      set(medallion, 'color', '#ab8c44');
      set(medallion, 'box-shadow', 'none');

      const body = card.querySelector('.tgo13-target-card__body');
      set(body, 'padding-top', matchMedia('(max-width: 749px)').matches ? '38px' : '40px');

      card.querySelectorAll('li').forEach((item) => {
        if (!item.querySelector('.tgo52-check-icon')) {
          item.prepend(makeIcon('<path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"/><path d="M9 12l2 2l4 -4"/>', 'tgo52-check-icon', 'circle-check'));
        }
        set(item, 'display', 'grid');
        set(item, 'grid-template-columns', '20px minmax(0, 1fr)');
        set(item, 'align-items', 'start');
        set(item, 'gap', '9px');
        set(item, 'padding', '0');
      });
    });
  }

  function apply() {
    if (!document.querySelector('.tgo13-hero--golden-one')) return;
    refineCards();
  }

  apply();
  addEventListener('load', apply, { once: true });
  addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 350);
  setTimeout(apply, 1200);
})();

/* TGO PDP V52b — keep Tabler glyph geometry above legacy icon guards */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');
  function apply() {
    if (!document.querySelector('.tgo13-hero--golden-one')) return;
    document.querySelectorAll('.tgo13-target-card__icon .tgo52-target-glyph').forEach((glyph) => {
      const size = matchMedia('(max-width: 749px)').matches ? '30px' : '32px';
      set(glyph, 'width', size);
      set(glyph, 'max-width', 'none');
      set(glyph, 'height', size);
      set(glyph, 'max-height', 'none');
      set(glyph, 'margin', '0');
      set(glyph, 'color', '#ab8c44');
      set(glyph, 'stroke-width', '1.35');
    });
  }
  apply();
  addEventListener('load', apply, { once: true });
  addEventListener('resize', apply, { passive: true });
  setTimeout(apply, 500);
  setTimeout(apply, 1400);
})();

/* TGO PDP V53 — semantic editorial comparison legend */
(() => {
  'use strict';

  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');

  function enhanceComparison() {
    if (!document.querySelector('.tgo13-hero--golden-one')) return;

    const shell = document.querySelector('.tgo13-compare > .tgo13-shell');
    set(shell, 'width', matchMedia('(max-width: 749px)').matches ? 'calc(100% - 28px)' : 'min(calc(100% - 64px), 1020px)');
    set(shell, 'max-width', '1020px');
    set(shell, 'margin-left', 'auto');
    set(shell, 'margin-right', 'auto');

    const corner = document.querySelector('.tgo35-mobile-compare__corner');
    if (corner && !corner.querySelector('.tgo53-corner-title')) {
      corner.textContent = '';
      const kicker = document.createElement('span');
      kicker.className = 'tgo53-corner-kicker';
      kicker.textContent = 'Im Vergleich';
      const title = document.createElement('strong');
      title.className = 'tgo53-corner-title';
      title.textContent = 'Nährstoffprofil';
      const copy = document.createElement('small');
      copy.className = 'tgo53-corner-copy';
      copy.textContent = 'Ausgewählte Inhaltsstoffe pro Tagesportion';
      corner.append(kicker, title, copy);
    }

    document.querySelectorAll('.tgo35-mobile-compare__product.is-golden, .tgo35-mobile-compare__value.is-golden, .tgo13-compare__detail-row > .is-tgo').forEach((node) => {
      set(node, 'background', '#fff');
      set(node, 'background-image', 'none');
    });
  }

  enhanceComparison();
  addEventListener('load', enhanceComparison, { once: true });
  addEventListener('resize', enhanceComparison, { passive: true });
  setTimeout(enhanceComparison, 450);
  setTimeout(enhanceComparison, 1300);
})();

/* TGO PDP V53b — keep the hero product visibly dominant in the comparison */
(() => {
  'use strict';
  const set = (node, prop, value) => node?.style.setProperty(prop, value, 'important');

  function sizeComparisonProducts() {
    if (!document.querySelector('.tgo13-hero--golden-one')) return;
    const mobile = matchMedia('(max-width: 749px)').matches;
    const golden = document.querySelector('.tgo35-mobile-compare__product.is-golden img');
    set(golden, 'width', mobile ? '120px' : '200px');
    set(golden, 'max-width', mobile ? '120px' : '200px');
    set(golden, 'height', mobile ? '122px' : '168px');
    set(golden, 'padding', '0');
    set(golden, 'box-sizing', 'border-box');
    set(golden, 'object-fit', 'contain');
    set(golden, 'background', 'transparent');
    set(golden, 'mix-blend-mode', 'multiply');
  }

  sizeComparisonProducts();
  addEventListener('load', sizeComparisonProducts, { once: true });
  addEventListener('resize', sizeComparisonProducts, { passive: true });
  setTimeout(sizeComparisonProducts, 500);
  setTimeout(sizeComparisonProducts, 1400);
})();


/* TGO PDP V54 — restore the shared gold-eyebrow headline system */
(() => {
  'use strict';

  function applyHeadlineSystem() {
    if (!document.querySelector('.tgo13-hero--golden-one, .golden-smartrr.tgo19-hero')) return;

    const whyKicker = document.querySelector('.tgo13-why .tgo13-heading > .tgo13-kicker');
    if (whyKicker) {
      whyKicker.hidden = false;
      whyKicker.removeAttribute('hidden');
    }

    const routineHeader = document.querySelector('.tgo13-quality .tgo13-quality__header--routine');
    if (!routineHeader) return;

    const headerKickers = [...routineHeader.querySelectorAll('.tgo13-kicker')];
    let primaryKicker = routineHeader.querySelector(':scope > .tgo13-kicker');

    if (!primaryKicker) {
      primaryKicker = document.createElement('p');
      primaryKicker.className = 'tgo13-kicker';
      routineHeader.prepend(primaryKicker);
    }

    primaryKicker.textContent = 'Deine 3-Schritte-Routine';
    primaryKicker.hidden = false;
    primaryKicker.removeAttribute('hidden');

    headerKickers.forEach((kicker) => {
      if (kicker !== primaryKicker) kicker.remove();
    });
  }

  applyHeadlineSystem();
  addEventListener('load', applyHeadlineSystem, { once: true });
  setTimeout(applyHeadlineSystem, 350);
  setTimeout(applyHeadlineSystem, 1200);
})();
